<template>
  <v-container
    fluid
    class="funder-details-container pt-2 grey lighten-2"
    :class="{ 'px-2': $vuetify.breakpoint.width <= 1600, 'pl-0': $vuetify.breakpoint.width > 1600 }"
  >
    <v-layout wrap align-content-start>
      <v-flex class="mr-1">
        <v-card class="mb-2">
          <v-card-title class="mb-2">
            <v-avatar class="secondary">
              <v-icon dark>fa-coins</v-icon>
            </v-avatar>
            <div class="ml-3">
              <h4 class="headline">Funder Product Details</h4>
              Applied to individual products only
            </div>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  @click="toggleMeAsRecipient"
                  text
                  icon
                  :color="recipient ? 'green' : 'grey'"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon large>far fa-eye</v-icon>
                </v-btn>
              </template>
              <span v-if="recipient">Stop watching this Funder's products</span>
              <span v-else>Watch this Funder's products</span>
            </v-tooltip>
          </v-card-title>
        </v-card>
      </v-flex>
    </v-layout>
    <v-layout wrap align-content-start>
      <v-flex d-flex md6>
        <v-card class="mb-2 mr-1">
          <v-card flat>
            <v-card-text>
              <v-container grid-list-md>
                <v-form lazy-validation>
                  <v-layout wrap>
                    <v-flex xs6>
                      <img :src="selectedProduct.logo" alt="Logo" class="responsive" />
                    </v-flex>
                    <v-flex xs6>
                      <image-edit-upload :url.sync="selectedProduct.logo" :path="'funder-images'"></image-edit-upload>
                    </v-flex>
                    <v-flex xs12>
                      <v-text-field label="Product Name" v-model="selectedProduct.name"></v-text-field>
                    </v-flex>
                    <v-flex xs12>
                      <v-text-field label="Phone" v-model="selectedProduct.phone"></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field label="Address Line 1" v-model="selectedProduct.address.line_1"></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field label="Address Line 2" v-model="selectedProduct.address.line_2"></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field label="Address Line 3" v-model="selectedProduct.address.line_3"></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field label="Address Line 4" v-model="selectedProduct.address.line_4"></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field label="Address Line 5" v-model="selectedProduct.address.line_5"></v-text-field>
                    </v-flex>
                    <v-flex xs6>
                      <v-text-field label="Postcode" v-model="selectedProduct.address.post_code"></v-text-field>
                    </v-flex>
                  </v-layout>
                </v-form>
              </v-container>
            </v-card-text>
          </v-card>
        </v-card>
      </v-flex>
      <v-flex d-flex md6>
        <v-card class="mb-2 mx-1" style="width: 100%">
          <v-card-text>
            <v-container grid-list-md>
              <v-form lazy-validation>
                <v-layout wrap v-if="UserHasRole('Platform')">
                  <v-flex xs12>
                    <v-select
                      label="Product type"
                      v-model="selectedProductType"
                      :items="ProductTypes"
                      item-text="name"
                      item-value="id"
                    ></v-select>
                  </v-flex>
                  <v-flex xs12>
                    <v-select
                      label="Journey"
                      v-model="selectedJourney"
                      :items="productJourneys"
                      item-text="name"
                      item-value="id"
                    ></v-select>
                  </v-flex>
                </v-layout>
                <v-layout wrap v-if="UserHasRole('Platform')">
                  <v-flex xs3>
                    <v-switch v-model="selectedProduct.enabled" label="Enabled"></v-switch>
                  </v-flex>
                  <v-flex xs3>
                    <v-switch v-model="selectedProduct.published" label="Publish On CMS"></v-switch>
                  </v-flex>
                  <v-flex xs3>
                    <v-switch v-model="selectedProduct.auto_accept" label="Auto Accept Enquiries"></v-switch>
                  </v-flex>
                  <v-flex xs3>
                    <v-switch v-model="selectedProduct.provide_quote" label="Product provides a quote"></v-switch>
                  </v-flex>
                </v-layout>
                <v-layout wrap v-if="UserHasRole('Platform')">
                  <v-flex xs12>
                    <v-text-field
                      label="Registered Company Name"
                      hint="Overwrites Funder Company Details"
                      persistent-hint
                      v-model="selectedProduct.registered_company_name"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs6>
                    <v-text-field
                      label="Registered Company Number"
                      hint="Overwrites Funder Company Details"
                      persistent-hint
                      v-model="selectedProduct.registered_company_number"
                    ></v-text-field>
                  </v-flex>
                  <v-flex xs6>
                    <v-select
                      :items="productTiers"
                      item-text="name"
                      item-value="id"
                      label="Product Tier"
                      v-model="selectedProduct.tier_id"
                    ></v-select>
                  </v-flex>
                  <v-flex xs12>
                    <v-text-field
                      label="Funder API Product Reference"
                      hint="Sent in API calls to identify the product"
                      persistent-hint
                      v-model="selectedProduct.funder_api_product_reference"
                    ></v-text-field>
                  </v-flex>
                </v-layout>
              </v-form>
            </v-container>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-layout wrap align-content-start>
      <v-flex class="mr-1">
        <v-card class="mb-2">
          <v-card-actions>
            <v-container grid-list-md>
              <v-layout wrap>
                <v-flex xs12 class="text-right">
                  <v-btn color="accent" @click="updateProductDetails()">Update Product Details</v-btn>
                </v-flex>
              </v-layout>
            </v-container>
          </v-card-actions>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import _ from 'lodash';
import ImageEditUpload from '../../Partners/ImageEditUpload';

export default {
  components: { ImageEditUpload },
  name: 'funder-details',
  props: ['selectedProduct'],
  data: () => ({
    recipient: false
  }),
  computed: {
    ...mapGetters(['JourneyTypes', 'ProductTiers', 'ProductTypes', 'SelectedFunder', 'UserId', 'UserHasRole']),
    productTiers() {
      return _.orderBy(this.ProductTiers, 'order');
    },
    selectedProductType: {
      get() {
        return this.selectedProduct.type.id;
      },
      set(id) {
        this.selectedProduct.product_type_id = id;
      }
    },
    productJourneys() {
      return _.isUndefined(this.JourneyTypes[1]) ? [] : _.orderBy(this.JourneyTypes[1].journeys, 'name');
    },
    selectedJourney: {
      get() {
        return _.isUndefined(this.selectedProduct.journey) ? '' : this.selectedProduct.journey.id;
      },
      set(id) {
        this.selectedProduct.journey_id = id;
      }
    }
  },
  methods: {
    ...mapActions(['getProductTiers', 'updateFunder', 'updateFunderUser']),
    updateProductDetails() {
      const updates = this.diff(this.selectedProduct, this.SelectedFunder);
      this.updateFunder({ id: this.selectedProduct.id, updates });
    },
    diff(object, base) {
      function changes(object, base) {
        return _.transform(object, (result, value, key) => {
          if (!_.isEqual(value, base[key])) {
            result[key] = _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
          }
        });
      }
      return changes(object, base);
    },
    isDefined(value) {
      return !_.isUndefined(value);
    },
    amIaRecipient() {
      const record = _.find(this.SelectedFunder.recipients, { id: this.UserId });
      this.recipient = this.isDefined(record);
    },
    toggleMeAsRecipient() {
      this.recipient = !this.recipient;
      console.log(this.recipient);
      this.updateFunderUser({
        id: this.UserId,
        updates: {
          isRecipient: this.recipient,
          product_id: this.SelectedFunder.id
        }
      });
    }
  },
  watch: {
    SelectedFunder: function() {
      this.amIaRecipient();
    }
  },
  created() {
    this.getProductTiers();
  }
};
</script>

<style lang="scss" scoped>
.funder-details-container {
  height: calc(100vh - 130px);
  max-height: calc(100vh - 130px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.responsive {
  max-width: 100%;
}
</style>
